import React, { memo } from 'react'
import { useTheme } from 'styled-components'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { checkIsValidFrequencyRange, Icon } from '../../../../../../../utils'
import {
    barrierWidth,
    threatConsequenceWidth,
    initialThreat,
    initialConsequence,
    threatConsequenceHeight,
    unfocusOpacity,
    THREAT_COSNEQUENCE_GAP_X
} from '../../../../../../../utils/constants'
import { IDiagramThreat, IDiagramConsequence } from '../../../../RiskDiagramEditor'
import { DeleteBottomContainer, PopupSVG, TextContainer, ThreatConsequenceContainer } from './styles'

interface Props {
    y: number
    type: 'THREAT' | 'CONSEQUENCE'
    threatConsequenceData: IDiagramThreat | IDiagramConsequence /* #TODO after rest ready type this */
    middleY: number
    x: number
    onThreatConsequenceClick: (type: 'THREAT' | 'CONSEQUENCE', k: IDiagramThreat | IDiagramConsequence) => void
    onDeleteClick: (type: 'THREAT' | 'CONSEQUENCE', index: number) => void
    focus: 'unfocus' | 'focus' | 'none'
    index: number
    canDelete: boolean
    showFrequency: boolean
}

const ThreatAndConsequenceEditor: React.FC<Props> = ({
    type,
    y,
    threatConsequenceData,
    middleY,
    x,
    onThreatConsequenceClick,
    onDeleteClick,
    focus,
    index,
    canDelete,
    showFrequency
}) => {
    const theme = useTheme()
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    return (
        <>
            {/* dashed line from pulse to end of threat or consequence  */}
            <path
                d={`M 0 ${middleY} L ${
                    type === 'THREAT' ? initialThreat + barrierWidth * 1.1 : initialConsequence - barrierWidth * 1.1
                } ${y} H ${type === 'THREAT' ? x - threatConsequenceWidth : x + threatConsequenceWidth}`}
                strokeWidth={2}
                stroke={theme['light-borders']}
                strokeDasharray="5,5"
                fill="none"
            />
            <ThreatConsequenceContainer
                transform={`translate(${
                    type === 'THREAT'
                        ? x - threatConsequenceWidth - THREAT_COSNEQUENCE_GAP_X
                        : x + THREAT_COSNEQUENCE_GAP_X
                }, ${y - threatConsequenceHeight / 2 - (type === 'CONSEQUENCE' ? 2 : 0)})`}
                id={String(threatConsequenceData?.id)}
                cursor="pointer"
                opacity={focus === 'unfocus' ? unfocusOpacity : 1}
            >
                {type === 'THREAT' ? (
                    <>
                        <path
                            d="M 157.32 67.422 L 143.904 125.11 C 143.405 127.256 141.211 128.998 139.008 128.998 L 3.992 128.998 C 1.789 128.998 0 127.21 0 125.006 L 0 3.994 C 0 1.79 1.789 0.002 3.992 0.002 L 139.008 0.002 C 141.211 0.002 143.405 1.744 143.904 3.89 L 157.32 61.578 C 157.696 63.191 157.696 65.809 157.32 67.422 Z"
                            fill="#3866B1"
                            onClick={() => onThreatConsequenceClick(type, threatConsequenceData)}
                            stroke={theme['blue-500']}
                            strokeWidth={focus === 'focus' ? 5 : 0}
                        />
                        {canDelete && (
                            <>
                                <rect x={-30} width={50} height={threatConsequenceHeight / 4} fill="transparent" />
                                {bowtieAdmin && (
                                    <DeleteBottomContainer>
                                        <Icon
                                            type="delete"
                                            color={theme['gray-200']}
                                            scale="1.5"
                                            auxliarColor={theme['red-500']}
                                            clickEvent={() => onDeleteClick(type, index)}
                                            x={-27}
                                        />

                                        <PopupSVG transform="translate(-185, -4)">
                                            <g transform={`translate(148, 4)`}>
                                                <rect
                                                    className="svg-popup-helper"
                                                    // x={87}
                                                    // y={-82}
                                                    height={16}
                                                    width={16}
                                                    transform="rotate(45)"
                                                    rx={2}
                                                    ry={2}
                                                />
                                            </g>
                                            <foreignObject width={150} height={30}>
                                                <div className={type}>
                                                    {i18n('Delete') +
                                                        ' ' +
                                                        i18n(type).charAt(0) +
                                                        i18n(type)
                                                            .slice(1)
                                                            .toLowerCase()}
                                                </div>
                                            </foreignObject>
                                        </PopupSVG>
                                    </DeleteBottomContainer>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <path
                            d="M 0.68 61.578 L 14.096 3.89 C 14.595 1.744 16.789 0.002 18.992 0.002 L 154.008 0.002 C 156.211 0.002 158 1.79 158 3.994 L 158 125.006 C 158 127.21 156.211 128.998 154.008 128.998 L 18.992 128.998 C 16.789 128.998 14.595 127.256 14.096 125.11 L 0.68 67.422 C 0.304 65.809 0.304 63.191 0.68 61.578 Z"
                            fill="#8768A3"
                            onClick={() => onThreatConsequenceClick(type, threatConsequenceData)}
                            stroke={theme['blue-500']}
                            strokeWidth={focus === 'focus' ? 5 : 0}
                        />
                        {canDelete && (
                            <>
                                <rect
                                    x={threatConsequenceWidth - 20}
                                    width={50}
                                    height={threatConsequenceHeight / 4}
                                    fill="transparent"
                                />
                                {bowtieAdmin && (
                                    <DeleteBottomContainer>
                                        <Icon
                                            type="delete"
                                            color={theme['gray-200']}
                                            scale="1.5"
                                            auxliarColor={theme['red-500']}
                                            x={threatConsequenceWidth + 5}
                                            clickEvent={() => onDeleteClick(type, index)}
                                        />

                                        <PopupSVG transform={`translate(${threatConsequenceWidth + 35}, -4)`}>
                                            <g transform={`translate(2, 4)`}>
                                                <rect
                                                    className="svg-popup-helper"
                                                    height={16}
                                                    width={16}
                                                    transform="rotate(45)"
                                                    rx={2}
                                                    ry={2}
                                                />
                                            </g>
                                            <foreignObject width={150} height={30}>
                                                <div className={type}>
                                                    {i18n('Delete') +
                                                        ' ' +
                                                        i18n(type).charAt(0) +
                                                        i18n(type)
                                                            .slice(1)
                                                            .toLowerCase()}
                                                </div>
                                            </foreignObject>
                                        </PopupSVG>
                                    </DeleteBottomContainer>
                                )}
                            </>
                        )}
                    </>
                )}

                <foreignObject
                    onClick={() => onThreatConsequenceClick(type, threatConsequenceData)}
                    x={type === 'THREAT' ? 10 : 25}
                    y={8}
                    width={threatConsequenceWidth - 33}
                    height={threatConsequenceHeight - 33}
                    style={{ overflow: 'hidden', fontSize: '14px' }}
                >
                    <TextContainer>{threatConsequenceData?.name}</TextContainer>
                </foreignObject>

                {type === 'THREAT' ? (
                    <text
                        x={10}
                        y={threatConsequenceHeight - 28}
                        textAnchor="start"
                        dominantBaseline="hanging"
                        fill="#fff"
                        fontWeight={400}
                        fontSize={10}
                    >
                        {showFrequency && (
                            <>
                                {i18n('Initial Freq.')}
                                {': '}
                                {`${
                                    checkIsValidFrequencyRange(
                                        (threatConsequenceData as IDiagramThreat)?.initialFrequency
                                    )
                                        ? Number(
                                              (threatConsequenceData as IDiagramThreat)?.initialFrequency
                                          ).toExponential(4)
                                        : '---'
                                }`}
                            </>
                        )}
                    </text>
                ) : (
                    <text
                        x={25}
                        y={threatConsequenceHeight - 28}
                        textAnchor="start"
                        dominantBaseline="hanging"
                        fill="#fff"
                        fontWeight={400}
                        fontSize={10}
                    >
                        {showFrequency && (
                            <>
                                {i18n('Threshold Freq.')}
                                {': '}
                                {`${
                                    checkIsValidFrequencyRange(
                                        (threatConsequenceData as IDiagramConsequence)?.thresholdFrequency
                                    )
                                        ? Number(
                                              (threatConsequenceData as IDiagramConsequence)?.thresholdFrequency
                                          ).toExponential(4)
                                        : '---'
                                }`}
                            </>
                        )}
                    </text>
                )}
            </ThreatConsequenceContainer>
        </>
    )
}

export default memo(ThreatAndConsequenceEditor)
